import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, ArrowLeft, Eye, Search, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AffiliateCases = () => {
  const navigate = useNavigate();
  const { afiliatToken, afiliatData } = useAuth();
  const [cases, setCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const isAdmin = afiliatData ? afiliatData.is_admin : false;

  const fetchCases = useCallback(async () => {
    try {
      const response = await fetch(`${API}/affiliate/cases`, {
        headers: { Authorization: `Bearer ${afiliatToken}` }
      });
      if (!response.ok) throw new Error("Failed to fetch");
      const data = await response.json();
      setCases(data);
      setFilteredCases(data);
    } catch (err) {
      toast.error("Eroare la încărcarea dosarelor");
    } finally {
      setLoading(false);
    }
  }, [afiliatToken]);

  useEffect(() => {
    if (!afiliatToken) {
      navigate("/affiliate/login");
      return;
    }
    fetchCases();
  }, [afiliatToken, navigate, fetchCases]);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    
    if (!query || query.length < 2) {
      setFilteredCases(cases);
      return;
    }

    setSearching(true);
    try {
      const response = await fetch(`${API}/affiliate/cases/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${afiliatToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFilteredCases(data);
      }
    } catch (err) {
      // Fallback to local filtering
      const q = query.toLowerCase();
      const filtered = cases.filter(c => 
        c.numar_dosar.toLowerCase().includes(q) ||
        (c.client && c.client.toLowerCase().includes(q)) ||
        (c.telefon && c.telefon.includes(q)) ||
        (c.cod_afiliat && c.cod_afiliat.toLowerCase().includes(q))
      );
      setFilteredCases(filtered);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setFilteredCases(cases);
  };

  const updateStatus = async (caseId, status) => {
    try {
      const response = await fetch(`${API}/affiliate/cases/${caseId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${afiliatToken}`
        },
        body: JSON.stringify({ status })
      });
      if (!response.ok) throw new Error("Failed to update");
      toast.success("Status actualizat!");
      fetchCases();
    } catch (err) {
      toast.error("Eroare la actualizare");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      "În verificare": "bg-blue-100 text-blue-700",
      "Acceptat": "bg-amber-100 text-amber-700",
      "În lucru": "bg-purple-100 text-purple-700",
      "Finalizat": "bg-emerald-500 text-white",
      "nou": "bg-blue-100 text-blue-700",
      "in_verificare": "bg-blue-100 text-blue-700",
      "acceptat": "bg-amber-100 text-amber-700",
      "in_lucru": "bg-purple-100 text-purple-700",
      "finalizat": "bg-emerald-500 text-white",
      "respins": "bg-red-100 text-red-700"
    };
    return styles[status] || "bg-gray-100 text-gray-700";
  };

  const statusOptions = ["nou", "in_verificare", "acceptat", "in_lucru", "finalizat", "respins"];
  const statusLabels = { nou: "Nou", in_verificare: "În verificare", acceptat: "Acceptat", in_lucru: "În lucru", finalizat: "Finalizat", respins: "Respins" };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="affiliate-cases">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/affiliate/dashboard" className="p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Link>
              <span className="font-bold text-gray-900">{isAdmin ? "Toate Dosarele" : "Dosarele Mele"}</span>
              {isAdmin && <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded">ADMIN</span>}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={isAdmin ? "Caută după ID, nume, telefon sau cod afiliat..." : "Caută după ID dosar..."}
              className="w-full h-12 pl-12 pr-12 rounded-xl border border-gray-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              data-testid="search-input"
            />
            {searchQuery && (
              <button 
                onClick={clearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-sm text-gray-500 mt-2">
              {searching ? "Se caută..." : `${filteredCases.length} rezultate pentru "${searchQuery}"`}
            </p>
          )}
        </div>

        {filteredCases.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchQuery 
                ? "Nu s-au găsit rezultate pentru căutarea ta." 
                : isAdmin 
                  ? "Nu există dosare în sistem." 
                  : "Nu ai încă dosare. Distribuie codul tău pentru a începe!"}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="cases-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase">ID Dosar</th>
                    <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                    {isAdmin && <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase">Client</th>}
                    {isAdmin && <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase">Telefon</th>}
                    {isAdmin && <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase">Cod Afiliat</th>}
                    {isAdmin && <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase">Docs</th>}
                    <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase">Comision</th>
                    <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCases.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm font-mono text-gray-900 font-medium">{c.numar_dosar}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{c.data}</td>
                      {isAdmin && <td className="px-4 py-4 text-sm text-gray-900">{c.client}</td>}
                      {isAdmin && <td className="px-4 py-4 text-sm text-gray-600">{c.telefon}</td>}
                      {isAdmin && <td className="px-4 py-4 text-sm font-mono text-emerald-600">{c.cod_afiliat}</td>}
                      {isAdmin && <td className="px-4 py-4 text-sm text-gray-600">{c.documente || 0}</td>}
                      <td className="px-4 py-4">
                        {isAdmin ? (
                          <select 
                            value={c.status} 
                            onChange={(e) => updateStatus(c.id, e.target.value)} 
                            className={`${getStatusBadge(c.status)} px-3 py-1 rounded-full text-xs font-medium border-0 cursor-pointer`}
                          >
                            {statusOptions.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
                          </select>
                        ) : (
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(c.status)}`}>{c.status}</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-emerald-600">{c.comision > 0 ? `${c.comision} lei` : "-"}</td>
                      <td className="px-4 py-4">
                        <Link to={`/affiliate/cases/${c.id}`} className="text-emerald-600 hover:text-emerald-700" data-testid={`view-case-${c.id}`}>
                          <Eye className="w-5 h-5" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AffiliateCases;
